import React, { useCallback, useEffect, useMemo } from 'react';
import { Loader } from 'src/@vymo/ui/atoms';
import { Banner, NoData } from 'src/@vymo/ui/blocks';
import Table from 'src/@vymo/ui/blocks/table/index';
import { Filters } from 'src/@vymo/ui/molecules';
import { FormVersion } from 'src/@vymo/ui/molecules/form/types';
import { useFilterPersistence } from 'src/hooks/useFilterPersistence';
import { getWorkflowActionState } from 'src/models/lmsConfig/selectors';
import { resetWorkflowAction } from 'src/models/lmsConfig/slice';
import {
  clearModuleButtons,
  setModuleButtons,
} from 'src/models/moduleButtons/slice';
import { WORKFLOW_FORM } from 'src/modules/constants';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setAxiosErrorDescriptionHandler } from 'src/workspace/axios';
import { getWorkspaceDialogs } from 'src/workspace/selectors';
import { renderDialog } from 'src/workspace/slice';
import {
  MODULES_WITHOUT_ADD_BUTTON,
  MODULES_WITHOUT_FILTER_SUPPORT,
} from './constants';
import { getGenericListDisplayName, handleRowClick } from './queries';
import {
  getGenericListFilterFields,
  getGenericListFilters,
  getGenericListLoading,
  getGenericListMetaData,
  getGenericListNotification,
  getGenericListState,
} from './selector';
import {
  resetFilters,
  setCurrentPageNumber,
  setError,
  setFilters,
  setGenericListPageSize,
} from './slice';
import { fetchModuleTableData } from './thunk';
import styles from './index.module.scss';

function GenericList(props) {
  const { module } = props;
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(getGenericListLoading);
  const { columns, rows: listData } = useAppSelector(getGenericListState);
  const { pageSize, currentPage, maxPages, totalCount } = useAppSelector(
    getGenericListMetaData,
  );
  const filterFields = useAppSelector(getGenericListFilterFields);
  const filters = useAppSelector(getGenericListFilters);
  const dialogs = useAppSelector(getWorkspaceDialogs);
  const workflowFormDialog = dialogs?.[WORKFLOW_FORM];
  const workflowActionState = useAppSelector(getWorkflowActionState);
  const {
    message,
    id: messageId,
    variant,
  } = useAppSelector(getGenericListNotification);

  const filterCount = useMemo(
    () =>
      Object.keys(filters || {}).filter(
        (key) => filters[key] != null && filters[key] !== '',
      ).length,
    [filters],
  );

  const handleFiltersChange = useCallback(
    (persistedFilters: Record<string, any>) => {
      dispatch(setFilters(persistedFilters));
    },
    [dispatch],
  );

  useFilterPersistence(
    `genericList_${module}`,
    filters || {},
    handleFiltersChange,
  );

  const handleApply = useMemo(
    () => (appliedFilters: Record<string, any>) => {
      dispatch(setFilters(appliedFilters));
      dispatch(setCurrentPageNumber(1));
      dispatch(fetchModuleTableData({ module }));
    },
    [dispatch, module],
  );

  const handleReset = useMemo(
    () => () => {
      dispatch(resetFilters());
      dispatch(setCurrentPageNumber(1));
      dispatch(fetchModuleTableData({ module }));
    },
    [dispatch, module],
  );

  const getModuleDisplayName = useCallback((moduleCode: string): string => {
    if (!moduleCode) return '';

    return moduleCode
      .split(/[-_]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  const handleAddClick = useCallback(() => {
    dispatch(
      renderDialog({
        id: WORKFLOW_FORM,
        props: {
          moduleCode: module,
          voId: 'global',
          action: 'add_lead',
        },
      }),
    );
  }, [dispatch, module]);

  // Set module buttons when component mounts (excluding DDP/bulk allocation)
  useEffect(() => {
    // Only set buttons if module is defined and supports add actions
    if (module && !MODULES_WITHOUT_ADD_BUTTON.includes(module)) {
      const moduleName = getModuleDisplayName(module);
      if (moduleName) {
        dispatch(
          setModuleButtons([
            {
              label: `Add ${moduleName}`,
              onClick: handleAddClick,
              variant: 'default',
              'data-test-id': `add-${module}-button`,
            },
          ]),
        );
      }
    } else if (MODULES_WITHOUT_ADD_BUTTON.includes(module)) {
      // Clear buttons for list-only modules
      dispatch(clearModuleButtons());
    }
    return () => {
      dispatch(clearModuleButtons());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, module]);

  // Refresh list when workflow form completes successfully and dialog closes
  useEffect(() => {
    if (workflowActionState === 'success' && !workflowFormDialog && module) {
      dispatch(fetchModuleTableData({ module }));
      dispatch(resetWorkflowAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowActionState, workflowFormDialog]);

  useEffect(
    () =>
      setAxiosErrorDescriptionHandler((errorDescription) => {
        dispatch(
          setError({
            message: errorDescription,
            id: String(Date.now()),
          }),
        );
      }),
    [dispatch],
  );

  const notificationBanner = message && (
    <Banner
      key={messageId}
      message={message}
      variant={variant}
      position="topRight"
      duration={5000}
      closeable
    />
  );

  if (!columns.length && !isLoading) {
    return (
      <>
        {notificationBanner}
        <NoData classNames={styles.genericList__pageBody} />
      </>
    );
  }

  return (
    <div className={styles.genericList}>
      {notificationBanner}
      <div className={styles.genericList__actions}>
        {filterFields.length > 0 &&
          !MODULES_WITHOUT_FILTER_SUPPORT.includes(module) && (
            <Filters
              filterFields={filterFields}
              metaData={{}}
              formVersion={FormVersion.webRevyre}
              filters={filters}
              filterCount={filterCount}
              onApply={handleApply}
              onReset={handleReset}
            />
          )}
      </div>

      <Loader visible={isLoading} className={styles.genericList__loader}>
        <Table
          data-test-id="download-list"
          isAccordion={false}
          tableData={listData}
          columnConfigs={columns}
          tableConfig={{
            showSearch: false,
            isRowSelectionEnabled: false,
            name: getGenericListDisplayName(module),
            pagination: {
              visible: true,
              pageSize,
              totalPages: maxPages,
              currentPage,
              totalItems: totalCount,
            },
            onRowClick: (rowId, rowData) => {
              handleRowClick({
                dispatch,
                module,
                rowId,
                rowData,
              });
            },
          }}
          onChangePageSize={(updatedPageSize) => {
            dispatch(setGenericListPageSize(updatedPageSize));
            dispatch(fetchModuleTableData({ module }));
          }}
          onChangePageIndex={(pageIndex) => {
            dispatch(setCurrentPageNumber(Number(pageIndex) + 1));
            dispatch(fetchModuleTableData({ module }));
          }}
        />
      </Loader>
    </div>
  );
}

export default GenericList;
